import type { KeyboardEvent } from 'react';
import { cn } from '@/shared/lib/cn';
import { BOOKING_DETAIL_TABS, type BookingDetailTab } from './bookingDetailTabModel';

interface Props {
  active: BookingDetailTab;
  /** Zahl am Reiter, z. B. Rechnungen unter Geld oder ein offenes Onboarding unter Gast. */
  counts: Partial<Record<BookingDetailTab, number>>;
  onChange: (tab: BookingDetailTab) => void;
}

/** Reiter im Kopf der Buchung; Pfeiltasten, Pos1 und Ende wechseln wie bei Tabs üblich. */
export function BookingDetailTabs({ active, counts, onChange }: Props) {
  function handleKeyDown(event: KeyboardEvent<HTMLButtonElement>, index: number) {
    const last = BOOKING_DETAIL_TABS.length - 1;
    const nextIndex =
      event.key === 'ArrowRight'
        ? index === last
          ? 0
          : index + 1
        : event.key === 'ArrowLeft'
          ? index === 0
            ? last
            : index - 1
          : event.key === 'Home'
            ? 0
            : event.key === 'End'
              ? last
              : null;
    if (nextIndex === null) return;
    event.preventDefault();
    const buttons =
      event.currentTarget.parentElement?.querySelectorAll<HTMLButtonElement>('[role="tab"]');
    buttons?.[nextIndex]?.focus();
    onChange(BOOKING_DETAIL_TABS[nextIndex]!.id);
  }

  return (
    <nav
      role="tablist"
      aria-label="Bereiche der Buchung"
      className="scrollbar-none overflow-x-auto overscroll-x-contain"
    >
      <div className="flex min-w-max gap-1">
        {BOOKING_DETAIL_TABS.map((tab, index) => {
          const selected = tab.id === active;
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={selected}
              aria-controls={`booking-panel-${tab.id}`}
              id={`booking-tab-${tab.id}`}
              tabIndex={selected ? 0 : -1}
              onClick={() => onChange(tab.id)}
              onKeyDown={(event) => handleKeyDown(event, index)}
              className={cn(
                'rounded-pill flex h-11 items-center gap-2 whitespace-nowrap px-4 text-[13px] font-bold',
                selected
                  ? 'bg-mint text-forest-deep'
                  : 'border border-white/20 text-white hover:bg-white/10',
              )}
            >
              {tab.label}
              {(counts[tab.id] ?? 0) > 0 && (
                <span
                  className={cn(
                    'rounded-pill inline-flex h-5 min-w-5 items-center justify-center px-1.5 text-[11px]',
                    selected ? 'bg-forest-deep text-white' : 'bg-white/15',
                  )}
                >
                  {counts[tab.id]}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </nav>
  );
}
